/**
 * SMS Composer modal — sends an SMS/MMS to a contact via Aircall.
 *
 * Feature parity target: old PHP CRM's deluxe composer.
 *   - Template picker (grouped by category)
 *   - Merge-field chips (clickable, shows resolved value for this contact)
 *   - Emoji picker (curated set)
 *   - Live preview bubble
 *   - GSM-7 / UCS-2 segment counter
 *   - Opt-out enforcement (server-side)
 *   - MMS attachments (upload → R2 → signed URL → Aircall)
 */
import { useMemo, useState, useEffect, useRef } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Loader2,
  Send,
  Sparkles,
  Smile,
  Hash,
  MessageSquare,
  AlertTriangle,
  X,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { communicationsApi, templatesApi, ApiError } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { formatPhone } from "@shared/utils/phone"
import { cn } from "@/lib/utils"

export type SmsComposerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact: {
    id: string
    firstName: string | null
    lastName: string | null
    businessName: string | null
    email?: string | null
    mobile: string | null
    businessPhone: string | null
    smsOptOut?: boolean
  }
  dealId?: string | null
}

// Curated emoji set (matches old PHP composer tone)
const EMOJIS = [
  "👍", "🙏", "👋", "❤️", "🔥", "⭐", "✅", "❌",
  "😊", "😁", "😂", "🤣", "😉", "😎", "🤔", "🙌",
  "💪", "👌", "👏", "🚀", "🎉", "📅", "📞", "💬",
  "📧", "📎", "📌", "🔔", "⏰", "✈️", "🚗", "🚚",
  "🏠", "🏢", "💰", "💵", "💳", "🤝", "🎯", "📈",
  "📉", "📊", "📝", "✍️", "✏️", "📚", "💻", "⚡",
  "🌟", "💡", "🔑", "🔓", "🛠️", "⚙️", "⚖️", "📄",
  "🎁", "🏆", "🕐", "📍", "🗓️", "✨",
]

// Merge fields exposed in the picker
const MERGE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "full_name", label: "Full name" },
  { key: "business_name", label: "Business" },
  { key: "contact_email", label: "Email" },
  { key: "contact_mobile", label: "Mobile" },
  { key: "broker_name", label: "Your name" },
  { key: "broker_phone", label: "Your phone" },
  { key: "broker_email", label: "Your email" },
]

// GSM-7 vs Unicode segmenter
function calcSegments(body: string): {
  segments: number
  perSegment: number
  encoding: "GSM-7" | "UCS-2"
} {
  const isUnicode = /[^\u0000-\u007F\u00A0-\u00FF]/.test(body)
  const perSegment = isUnicode ? 70 : 160
  const len = body.length
  if (len === 0) return { segments: 1, perSegment, encoding: isUnicode ? "UCS-2" : "GSM-7" }
  const perConcat = isUnicode ? 67 : 153
  const segments = len <= perSegment ? 1 : Math.ceil(len / perConcat)
  return { segments, perSegment, encoding: isUnicode ? "UCS-2" : "GSM-7" }
}

// Client-side merge preview — mirrors server logic in merge-fields.ts
function renderMergePreview(
  source: string,
  ctx: {
    contact: SmsComposerProps["contact"]
    broker: { fullName: string | null; email: string | null; phone: string | null }
  }
): string {
  const values: Record<string, string | null | undefined> = {
    first_name: ctx.contact.firstName,
    last_name: ctx.contact.lastName,
    full_name: [ctx.contact.firstName, ctx.contact.lastName].filter(Boolean).join(" ").trim() || null,
    business_name: ctx.contact.businessName,
    contact_email: ctx.contact.email ?? null,
    contact_mobile: ctx.contact.mobile,
    broker_name: ctx.broker.fullName,
    broker_email: ctx.broker.email,
    broker_phone: ctx.broker.phone,
  }
  return source.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, key: string) => {
    const k = key.toLowerCase()
    const v = values[k]
    return v == null ? "" : String(v)
  })
}

// ============================================================
// Component
//
// MMS was intentionally removed — Aircall's API doesn't allow a single
// number to send both via the Aircall app AND via Public API (which is
// required for MMS). We chose to keep the Aircall app working. To send
// an image, use the Aircall app directly.
// ============================================================
export function SmsComposer({ open, onOpenChange, contact, dealId }: SmsComposerProps) {
  const { user: currentUser } = useAuth()
  const qc = useQueryClient()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [body, setBody] = useState("")
  const [templateId, setTemplateId] = useState<string>("")
  const [applyMerge, setApplyMerge] = useState(true)
  const [panel, setPanel] = useState<"merge" | "emoji" | null>(null)
  const [error, setError] = useState<string | null>(null)

  const toNumber = contact.mobile ?? contact.businessPhone ?? null
  // Prefer the person's name over their business name in the composer header.
  // Businesses don't receive SMS; people do.
  const personName = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim()
  const contactDisplayName =
    personName || contact.businessName || "this contact"

  const templatesQ = useQuery({
    queryKey: ["templates", "sms"],
    queryFn: () => templatesApi.list("sms"),
    enabled: open,
  })
  const templates = templatesQ.data?.items ?? []
  const selectedTemplate = templates.find((t) => t.id === templateId)

  // Reset on open
  useEffect(() => {
    if (open) {
      setBody("")
      setTemplateId("")
      setApplyMerge(true)
      setPanel(null)
      setError(null)
    }
  }, [open])

  // When template picked, pre-fill body
  useEffect(() => {
    if (selectedTemplate) {
      setBody(selectedTemplate.body)
    }
  }, [selectedTemplate])

  const send = useMutation({
    mutationFn: () =>
      communicationsApi.sendSms({
        contactId: contact.id,
        dealId: dealId ?? null,
        body: templateId ? undefined : body.trim() || undefined,
        templateId: templateId || undefined,
        applyMerge,
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        setError(r.message ?? r.error ?? "Aircall rejected the message.")
        return
      }
      qc.invalidateQueries({ queryKey: ["communications"] })
      qc.invalidateQueries({ queryKey: ["contact", contact.id] })
      onOpenChange(false)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to send SMS.")
    },
  })

  const segInfo = useMemo(() => calcSegments(body), [body])

  const preview = useMemo(() => {
    if (!body) return ""
    if (!applyMerge) return body
    return renderMergePreview(body, {
      contact,
      broker: {
        fullName: currentUser?.fullName ?? null,
        email: currentUser?.email ?? null,
        phone: null,
      },
    })
  }, [body, applyMerge, contact, currentUser])

  const isOptedOut = !!contact.smsOptOut
  const noNumber = !toNumber

  const hasText = body.trim().length > 0
  const hasContentSource = hasText || (templateId && templates.length > 0)

  const canSend =
    !send.isPending &&
    !isOptedOut &&
    !noNumber &&
    hasContentSource

  const insertAtCursor = (text: string) => {
    const ta = textareaRef.current
    if (!ta) {
      setBody((b) => b + text)
      return
    }
    const start = ta.selectionStart ?? body.length
    const end = ta.selectionEnd ?? body.length
    const next = body.slice(0, start) + text + body.slice(end)
    setBody(next)
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + text.length
      ta.setSelectionRange(pos, pos)
    })
    if (templateId && next !== selectedTemplate?.body) {
      setTemplateId("")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Send SMS
          </DialogTitle>
          <DialogDescription>
            To <strong>{contactDisplayName}</strong>
            {toNumber && (
              <>
                {" · "}
                <span className="font-mono text-xs">{formatPhone(toNumber)}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isOptedOut && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              This contact has opted out of SMS. You cannot send them a message.
            </div>
          </div>
        )}
        {noNumber && !isOptedOut && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              This contact has no mobile or phone number. Add one before sending
              an SMS.
            </div>
          </div>
        )}

        {templates.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="sms-tpl" className="text-muted-foreground text-xs uppercase tracking-wide">
              Template
            </Label>
            <div className="flex items-center gap-2">
              <select
                id="sms-tpl"
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value)
                  if (!e.target.value) setBody("")
                }}
                className="border-input bg-background block flex-1 rounded-md border px-3 py-2 text-sm"
                disabled={send.isPending}
              >
                <option value="">— None (write freeform) —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.category ? `[${t.category}] ` : ""}
                    {t.name}
                  </option>
                ))}
              </select>
              {templateId && (
                <label className="text-muted-foreground flex items-center gap-1.5 whitespace-nowrap text-xs">
                  <input
                    type="checkbox"
                    checked={applyMerge}
                    onChange={(e) => setApplyMerge(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  <Sparkles className="h-3 w-3 text-purple-500" />
                  Merge fields
                </label>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="sms-body" className="text-muted-foreground text-xs uppercase tracking-wide">
              Message
            </Label>
            <span
              className={cn(
                "text-xs tabular-nums",
                segInfo.segments > 3
                  ? "text-destructive font-medium"
                  : segInfo.segments > 1
                    ? "text-amber-600"
                    : "text-muted-foreground"
              )}
              title={`${segInfo.encoding} encoding, ${segInfo.perSegment} chars per segment`}
            >
              {body.length} chars · {segInfo.segments} segment
              {segInfo.segments === 1 ? "" : "s"}
            </span>
          </div>

          {/* Toolbar */}
          <div className="bg-rc-grey-50 flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-border px-2 py-1.5">
            <button
              type="button"
              onClick={() => setPanel(panel === "merge" ? null : "merge")}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-white",
                panel === "merge" && "bg-white ring-1 ring-blue-200"
              )}
              disabled={send.isPending}
            >
              <Hash className="h-3.5 w-3.5 text-blue-600" />
              Fields
            </button>
            <button
              type="button"
              onClick={() => setPanel(panel === "emoji" ? null : "emoji")}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-white",
                panel === "emoji" && "bg-white ring-1 ring-blue-200"
              )}
              disabled={send.isPending}
            >
              <Smile className="h-3.5 w-3.5 text-amber-500" />
              Emoji
            </button>
            {/*
             * MMS button hidden — Aircall's API requires configuring the number
             * for Public API, which permanently disables sending SMS from the
             * Aircall app on that number. We chose to keep the Aircall app
             * working; MMS from CRM isn't possible with Aircall's current API.
             *
             * If you need to send an image, use the Aircall app directly.
             * Alternatively add ClickSend or another MMS provider (creds
             * already available in TOOLS.md).
             */}
          </div>

          <Textarea
            id="sms-body"
            ref={textareaRef}
            rows={5}
            value={body}
            onChange={(e) => {
              setBody(e.target.value)
              if (templateId && e.target.value !== selectedTemplate?.body) {
                setTemplateId("")
              }
            }}
            placeholder={
              templates.length
                ? "Pick a template above or write your own message…"
                : "Type your SMS…"
            }
            disabled={send.isPending || isOptedOut || noNumber}
            className="rounded-t-none border-t-0 font-mono text-sm"
          />

          {/* Panels */}
          {panel === "merge" && (
            <div className="rounded-md border border-border bg-white p-2">
              <div className="text-muted-foreground mb-1.5 text-[10px] uppercase tracking-wide">
                Click to insert. Shows resolved value for this contact.
              </div>
              <div className="flex flex-wrap gap-1.5">
                {MERGE_FIELDS.map((f) => {
                  const resolved = renderMergePreview(`{{${f.key}}}`, {
                    contact,
                    broker: {
                      fullName: currentUser?.fullName ?? null,
                      email: currentUser?.email ?? null,
                      phone: null,
                    },
                  })
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => insertAtCursor(`{{${f.key}}}`)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 text-xs hover:border-blue-300 hover:bg-blue-50"
                    >
                      <span className="font-mono text-blue-700">
                        {"{{"}
                        {f.key}
                        {"}}"}
                      </span>
                      {resolved && (
                        <span className="text-muted-foreground max-w-[120px] truncate">
                          → {resolved}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {panel === "emoji" && (
            <div className="rounded-md border border-border bg-white p-2">
              <div className="grid grid-cols-12 gap-1">
                {EMOJIS.map((e, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => insertAtCursor(e)}
                    className="hover:bg-rc-grey-100 rounded p-1 text-lg"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Live preview */}
        {body && (
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs uppercase tracking-wide">
              Preview · what they'll see
            </Label>
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-blue-500 px-3 py-2 text-sm text-white shadow-sm">
                <div className="whitespace-pre-wrap break-words">
                  {preview || <span className="opacity-60">(empty)</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-3 text-sm">
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={send.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              setError(null)
              send.mutate()
            }}
            disabled={!canSend}
            className="gap-1.5"
          >
            {send.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send SMS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
